import { useEffect } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';

import wordmarkPng from '../assets/brand/wordmark.png';
import wordmarkWebp from '../assets/brand/wordmark.webp';
import { useOnline } from '../hooks/useOverlay';
import { useCartCount } from '../stores/cart-store';
import { useUiStore } from '../stores/ui-store';
import { CartDrawer } from './CartDrawer';
import { OfflineBanner } from './States';

/**
 * The application shell.
 *
 * Deliberately thin: a sticky header of four links, the route, and a footer.
 * Minimal navigation is a brand decision as much as a usability one - a
 * mega-menu would read as a catalogue, and this is meant to read as a house.
 */

const NAV = [
  { to: '/shop', label: 'Shop' },
  { to: '/journal', label: 'Journal' },
  { to: '/about', label: 'About' },
] as const;

function Header() {
  const count = useCartCount();
  const openCart = useUiStore((state) => state.openCart);
  const mobileNavOpen = useUiStore((state) => state.mobileNavOpen);
  const toggleMobileNav = useUiStore((state) => state.toggleMobileNav);

  return (
    <header className="header">
      <div className="container header__inner">
        <Link to="/" className="header__brand" aria-label="No Saints Club, home">
          {/* webp with a png fallback. The mark is a soft glow, which png
              compresses poorly and webp handles in a third of the bytes.
              Dimensions are set to reserve the space and avoid layout shift,
              and it is eager/high priority because it is above the fold. */}
          <picture>
            <source srcSet={wordmarkWebp} type="image/webp" />
            <img
              src={wordmarkPng}
              alt="No Saints Club"
              className="header__logo"
              width={512}
              height={97}
              fetchPriority="high"
            />
          </picture>
        </Link>

        <nav className="nav" aria-label="Primary">
          {NAV.map((item) => (
            <NavLink key={item.to} to={item.to} className="nav__link">
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="header__actions">
          <Link to="/search" className="icon-button">
            Search
          </Link>
          <Link to="/account" className="icon-button">
            <span className="visually-hidden">Account</span>
            <span aria-hidden="true">Account</span>
          </Link>
          <button
            type="button"
            className="icon-button"
            onClick={openCart}
            aria-haspopup="dialog"
            aria-label={`Bag, ${count} ${count === 1 ? 'item' : 'items'}`}
          >
            Bag
            {count > 0 ? (
              <span className="cart-count" aria-hidden="true">
                {count}
              </span>
            ) : null}
          </button>
          <button
            type="button"
            className="icon-button"
            style={{ display: 'inline-flex' }}
            data-mobile-only
            onClick={() => toggleMobileNav()}
            aria-expanded={mobileNavOpen}
            aria-controls="mobile-nav"
          >
            {mobileNavOpen ? 'Close' : 'Menu'}
          </button>
        </div>
      </div>

      {mobileNavOpen ? (
        <nav id="mobile-nav" className="container" aria-label="Mobile" style={{ paddingBottom: '1.5rem' }}>
          <ul className="stack">
            {NAV.map((item) => (
              <li key={item.to}>
                <NavLink to={item.to} className="nav__link" onClick={() => toggleMobileNav(false)}>
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
      ) : null}
    </header>
  );
}

function Footer() {
  return (
    <footer className="footer">
      <div className="container">
        <div className="footer__grid">
          <div className="footer__col">
            <h2 className="label label--bright">Shop</h2>
            <div className="footer__links">
              <Link to="/shop">All pieces</Link>
              <Link to="/collection/latest">Latest drop</Link>
              <Link to="/search">Search</Link>
            </div>
          </div>
          <div className="footer__col">
            <h2 className="label label--bright">Orders</h2>
            <div className="footer__links">
              <Link to="/track">Track an order</Link>
              <Link to="/shipping">Shipping</Link>
              <Link to="/returns">Returns</Link>
              <Link to="/account">Account</Link>
            </div>
          </div>
          <div className="footer__col">
            <h2 className="label label--bright">House</h2>
            <div className="footer__links">
              <Link to="/about">About</Link>
              <Link to="/journal">Journal</Link>
              <a href="https://instagram.com/nosaints.club" target="_blank" rel="noreferrer noopener">
                Instagram
              </a>
            </div>
          </div>
          <div className="footer__col">
            <h2 className="label label--bright">Legal</h2>
            <div className="footer__links">
              <Link to="/privacy">Privacy</Link>
              <Link to="/terms">Terms</Link>
              <a href="mailto:contact@nosaints.club">contact@nosaints.club</a>
            </div>
          </div>
        </div>

        <div className="footer__base">
          <p className="label">© {new Date().getFullYear()} No Saints Club</p>
          <p className="label">
            a brand of{' '}
            {/* External destination: noopener/noreferrer so the target cannot
                reach back through window.opener. */}
            <a
              className="footer__legal-link"
              href="https://throwdownmedia.net"
              target="_blank"
              rel="noopener noreferrer"
            >
              THROWDOWN MEDIA LLP
            </a>
          </p>
          <p className="label">Timeless World Wide</p>
        </div>
      </div>
    </footer>
  );
}

/** Scroll to top on navigation - a router does not do this for you. */
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [pathname]);
  return null;
}

/** Announces route changes, which are otherwise silent for screen readers. */
function RouteAnnouncer() {
  const announcement = useUiStore((state) => state.announcement);
  return (
    <p aria-live="polite" role="status" className="visually-hidden">
      {announcement}
    </p>
  );
}

export function Layout() {
  const online = useOnline();

  return (
    <div className="shell">
      <a className="skip-link" href="#main">
        Skip to content
      </a>
      <OfflineBanner online={online} />
      <ScrollToTop />
      <RouteAnnouncer />
      <Header />
      <main id="main" className="shell__main" tabIndex={-1}>
        <Outlet />
      </main>
      <Footer />
      <CartDrawer />
    </div>
  );
}
